import React, { useMemo } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import CurrencyFormat from "react-currency-format";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { ThirdPartyService } from "../types/ThirdPartyService";
import useStyles from "../styles/useStyles";

interface Props {
  services: ThirdPartyService[];
  currencyUnit: string;
  isLoading: boolean;
}

const Show: React.FC<Props> = ({ services, currencyUnit, isLoading }) => {
  const styles = useStyles();

  const displayRows: ThirdPartyService[] = useMemo(() => {
    if (services.length === 0) return services;

    const total = services.reduce(
      (acc, s) => ({
        oneTimeRevenue: acc.oneTimeRevenue + s.oneTimeRevenue,
        recurringRevenue: acc.recurringRevenue + s.recurringRevenue,
        totalRevenue: acc.totalRevenue + s.totalRevenue,
        oneTimeCost: acc.oneTimeCost + s.oneTimeCost,
        recurringCost: acc.recurringCost + s.recurringCost,
        profit: acc.profit + s.profit,
      }),
      {
        oneTimeRevenue: 0,
        recurringRevenue: 0,
        totalRevenue: 0,
        oneTimeCost: 0,
        recurringCost: 0,
        profit: 0,
      }
    );

    const totalMargin =
      total.totalRevenue > 0
        ? (total.profit / total.totalRevenue) * 100
        : 0;

    return [
      ...services,
      {
        id: "total",
        vendorName: "TOTAL",
        description: "",
        oneTimeRevenue: total.oneTimeRevenue,
        recurringRevenue: total.recurringRevenue,
        totalRevenue: total.totalRevenue,
        oneTimeCost: total.oneTimeCost,
        recurringCost: total.recurringCost,
        profit: total.profit,
        margin: totalMargin,
        isTotal: true,
      },
    ];
  }, [services]);

  const formatCurrency = (value: number) => (
    <CurrencyFormat
      displayType="text"
      prefix={currencyUnit}
      isNumericString={true}
      thousandSeparator={true}
      value={value.toFixed(2)}
    />
  );

  const totalStyle = { fontWeight: 700 as const };

  const columns: ColumnDef<ThirdPartyService>[] = [
    {
      header: "Partner / Vendor",
      cell(props) {
        const row = props.row.original;
        if (row.isTotal) return null;
        return <span>{row.vendorName}</span>;
      },
    },
    {
      header: "Description",
      cell(props) {
        const row = props.row.original;
        if (row.isTotal)
          return <span style={{ fontWeight: 700 }}>TOTAL</span>;
        return <span>{row.description}</span>;
      },
    },
    {
      header: "One Time Revenue",
      cell(props) {
        const row = props.row.original;
        return (
          <span style={row.isTotal ? totalStyle : undefined}>
            {formatCurrency(row.oneTimeRevenue)}
          </span>
        );
      },
    },
    {
      header: "Recurring Revenue",
      cell(props) {
        const row = props.row.original;
        return (
          <span style={row.isTotal ? totalStyle : undefined}>
            {formatCurrency(row.recurringRevenue)}
          </span>
        );
      },
    },
    {
      header: "Total Value",
      cell(props) {
        const row = props.row.original;
        return (
          <span style={row.isTotal ? totalStyle : undefined}>
            {formatCurrency(row.totalRevenue)}
          </span>
        );
      },
    },
    {
      header: "One Time Cost",
      cell(props) {
        const row = props.row.original;
        return (
          <span style={row.isTotal ? { ...totalStyle, color: "#b90200" } : undefined}>
            {formatCurrency(row.oneTimeCost)}
          </span>
        );
      },
    },
    {
      header: "Recurring Cost",
      cell(props) {
        const row = props.row.original;
        return (
          <span style={row.isTotal ? { ...totalStyle, color: "#b90200" } : undefined}>
            {formatCurrency(row.recurringCost)}
          </span>
        );
      },
    },
    {
      header: "Profit",
      cell(props) {
        const row = props.row.original;
        return (
          <span style={row.isTotal ? { ...totalStyle, color: "#418172" } : undefined}>
            {formatCurrency(row.profit)}
          </span>
        );
      },
    },
    {
      header: "Margin",
      cell(props) {
        const row = props.row.original;
        const margin = row.margin;
        return (
          <span style={row.isTotal ? { ...totalStyle, color: "#418172" } : undefined}>
            {margin ? `${margin.toFixed(2)}%` : "0.00%"}
          </span>
        );
      },
    },
  ];

  return (
    <div className={`${styles.page} ${styles.rightAlignColumns}`}>
      <Card className={styles.card}>
        <Card.Header className={styles.cardHeader}>
          <Row className={styles.headerRow}>
            <Col className={styles.title}>Third Party Services Summary</Col>
          </Row>
        </Card.Header>
        <hr className={styles.hr} />
        <Card.Body>
          {isLoading ? (
            <ScopeStackSpinner />
          ) : (
            <DataTable
              data={displayRows}
              columns={columns}
              selectable={false}
              striped
              hover
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Show;
